import React, { useState, useEffect, useMemo } from "react";

type ScreenSize = "sm" | "md" | "lg";
type ResponsiveValue<T> = T | Partial<Record<ScreenSize, T>>;

interface FlexboxProps {
  direction?: ResponsiveValue<"row" | "column" | "row-reverse" | "column-reverse">;
  align?: ResponsiveValue<"flex-start" | "flex-end" | "center" | "stretch" | "baseline">;
  justify?: ResponsiveValue<
    "flex-start" | "flex-end" | "center" | "space-between" | "space-around" | "space-evenly"
  >;
  wrap?: ResponsiveValue<"nowrap" | "wrap" | "wrap-reverse">;
  gap?: ResponsiveValue<number | string>;
  padding?: ResponsiveValue<number | string>;
  margin?: ResponsiveValue<number | string>;
  backgroundColor?: ResponsiveValue<string>;
  width?: ResponsiveValue<string>;
  maxWidth?: ResponsiveValue<string>;
  height?: ResponsiveValue<string>;
  borderRadius?: ResponsiveValue<string>;
  border?: ResponsiveValue<string>;
  boxShadow?: ResponsiveValue<string>;
  overflow?: ResponsiveValue<"visible" | "hidden" | "auto" | "scroll">;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string; // for Tailwind or other classes
}

const getScreenSize = (width: number): ScreenSize => {
  if (width < 768) return "sm";
  if (width < 1024) return "md";
  return "lg";
};

const resolveResponsive = <T,>(
  prop: ResponsiveValue<T> | undefined,
  screen: ScreenSize,
  fallback?: T
): T | undefined => {
  if (prop == null) return fallback;
  if (typeof prop !== "object") return prop;
  return (prop as Partial<Record<ScreenSize, T>>)[screen] ?? fallback;
};

export const Flexbox: React.FC<FlexboxProps> = ({
  direction = { sm: "column", md: "row", lg: "row" },
  align = "center",
  justify = "space-between",
  wrap = "wrap",
  gap = 16,
  padding,
  margin,
  backgroundColor = "transparent",
  width = "100%",
  maxWidth = "100%",
  height = "auto",
  borderRadius,
  border,
  boxShadow,
  overflow,
  children,
  style,
  className,
}) => {
  const [screen, setScreen] = useState<ScreenSize>("lg");

  useEffect(() => {
    const updateScreen = () => setScreen(getScreenSize(window.innerWidth));
    updateScreen();
    window.addEventListener("resize", updateScreen);
    return () => window.removeEventListener("resize", updateScreen);
  }, []);

  const computedStyle = useMemo<React.CSSProperties>(() => {
    const toCssValue = (val?: string | number) =>
      typeof val === "number" ? `${val}px` : val;

    return {
      display: "flex",
      flexDirection: resolveResponsive(direction, screen, "row"),
      alignItems: resolveResponsive(align, screen, "center"),
      justifyContent: resolveResponsive(justify, screen, "flex-start"),
      flexWrap: resolveResponsive(wrap, screen, "wrap"),
      gap: toCssValue(resolveResponsive(gap, screen, undefined)),
      padding: toCssValue(resolveResponsive(padding, screen, undefined)),
      margin: toCssValue(resolveResponsive(margin, screen, undefined)),
      backgroundColor: resolveResponsive(backgroundColor, screen, undefined),
      width: resolveResponsive(width, screen, "100%"),
      maxWidth: resolveResponsive(maxWidth, screen, undefined),
      height: resolveResponsive(height, screen, undefined),
      borderRadius: resolveResponsive(borderRadius, screen, undefined),
      border: resolveResponsive(border, screen, undefined),
      boxShadow: resolveResponsive(boxShadow, screen, undefined),
      overflow: resolveResponsive(overflow, screen, undefined),
      boxSizing: "border-box",
      ...style,
    };
  }, [
    direction,
    align,
    justify,
    wrap,
    gap,
    padding,
    margin,
    backgroundColor,
    width,
    maxWidth,
    height,
    borderRadius,
    border,
    boxShadow,
    overflow,
    screen,
    style,
  ]);

  return (
    <div className={className} style={computedStyle}>
      {children}
    </div>
  );
};
